// Splits a scene's prose into reader-paced segments (roughly one sentence each)
// for the stepped "Next / Back" reading flow and the editor's section timeline.
// Works on character spans of the original text so every segment also knows how
// it was separated from the previous one (a space, a line break, a paragraph) —
// which lets a timeline of sections be joined back into the same prose.
import type { SectionBreak } from "./types.js";

// Segments shorter than this get merged into the following one so the reader is
// never stepped through a lone word or a two-word fragment.
const MIN_SEGMENT_LENGTH = 40;
// Upper bound so one long sentence/beat can't grow the fixed reading panel and
// so long prose paginates into more frames. 150 chars wraps to <=4 lines on a
// ~375px phone and <=2 lines on desktop (reading measure capped at 46rem) —
// always within .player-reading's reserved min-height, so revealing a beat never
// resizes the panel.
export const MAX_SEGMENT_LENGTH = 150;

export interface SceneTextSegment {
  text: string;
  /** How this segment is separated from the previous one (absent = a space). */
  break_before?: SectionBreak;
}

/** A [start, end) range of the source text. */
interface Span {
  start: number;
  end: number;
}

/**
 * Break scene text into ordered display segments. Prefers the locale-aware
 * Intl.Segmenter (correct for Russian, dialogue, and most languages); falls back
 * to a punctuation regex where the API is unavailable. Always returns at least
 * one segment for non-empty text.
 */
export function segmentSceneText(text: string, language: string): string[] {
  return segmentSceneTextDetailed(text, language).map((segment) => segment.text);
}

/** `segmentSceneText` plus the separator before each segment. */
export function segmentSceneTextDetailed(text: string, language: string): SceneTextSegment[] {
  const source = text.replace(/\r\n?/g, "\n");
  if (!source.trim()) return [];

  const sentences = splitIntoSentences(source, language);
  const merged = mergeShortSpans(source, sentences);
  // Bound AFTER merging: merging can append a sub-MIN fragment onto a following
  // sentence, which could otherwise re-exceed MAX. Splitting last keeps the
  // guarantee that every returned segment is <= MAX_SEGMENT_LENGTH.
  const bounded = merged.flatMap((span) =>
    spanLength(source, span) <= MAX_SEGMENT_LENGTH ? [span] : splitLongSpan(source, span)
  );

  return bounded.map((span, index) => {
    const segment: SceneTextSegment = { text: spanText(source, span) };
    if (index > 0) {
      const gap = source.slice(bounded[index - 1].end, span.start);
      const kind = breakKind(gap);
      if (kind) segment.break_before = kind;
    }
    return segment;
  });
}

/** The separator a `SectionBreak` stands for when prose is re-joined. */
export function breakSeparator(kind: SectionBreak | undefined): string {
  if (kind === "paragraph") return "\n\n";
  if (kind === "line") return "\n";
  return " ";
}

function breakKind(gap: string): SectionBreak | undefined {
  const newlines = (gap.match(/\n/g) ?? []).length;
  if (newlines >= 2) return "paragraph";
  if (newlines === 1) return "line";
  return undefined;
}

/** Span text with whitespace runs collapsed: a run holding a newline stays one newline. */
function spanText(source: string, span: Span): string {
  return source
    .slice(span.start, span.end)
    .replace(/\s+/gu, (run) => (run.includes("\n") ? "\n" : " "))
    .trim();
}

function spanLength(source: string, span: Span): number {
  return spanText(source, span).length;
}

/** Shrink a span so it starts and ends on non-whitespace; null when empty. */
function trimSpan(source: string, start: number, end: number): Span | null {
  let s = start;
  let e = end;
  while (s < e && /\s/u.test(source[s])) s += 1;
  while (e > s && /\s/u.test(source[e - 1])) e -= 1;
  return s < e ? { start: s, end: e } : null;
}

/** Split a span at every match of `separator` (a global regex matching whitespace runs). */
function splitSpanAt(source: string, span: Span, separator: RegExp): Span[] {
  const slice = source.slice(span.start, span.end);
  const parts: Span[] = [];
  let cursor = 0;
  for (const match of slice.matchAll(separator)) {
    const index = match.index ?? 0;
    const piece = trimSpan(source, span.start + cursor, span.start + index);
    if (piece) parts.push(piece);
    cursor = index + match[0].length;
  }
  const tail = trimSpan(source, span.start + cursor, span.end);
  if (tail) parts.push(tail);
  return parts.length > 0 ? parts : [span];
}

// Break a span that exceeds MAX_SEGMENT_LENGTH into pieces that each fit,
// preferring clause boundaries and falling back to word boundaries. Never splits
// inside a word.
function splitLongSpan(source: string, span: Span): Span[] {
  const atoms: Span[] = [];
  // Break after a clause mark (comma, semicolon, colon, en/em dash, ellipsis)
  // followed by whitespace, keeping the mark with the left fragment.
  for (const clause of splitSpanAt(source, span, /(?<=[,;:—–…])\s+/gu)) {
    if (spanLength(source, clause) <= MAX_SEGMENT_LENGTH) {
      atoms.push(clause);
    } else {
      // Whitespace split never breaks inside a word. With no whitespace at all
      // the clause stays unbroken rather than cut mid-word.
      atoms.push(...splitSpanAt(source, clause, /\s+/gu));
    }
  }
  return packAtoms(source, atoms);
}

// Greedily join adjacent atoms up to the cap. A too-short trailing piece is left
// as its own beat: once the packer starts a new piece it is because the previous
// one was already at the cap, so merging the two back would exceed
// MAX_SEGMENT_LENGTH and break the fixed reading-panel guarantee.
function packAtoms(source: string, atoms: Span[]): Span[] {
  const pieces: Span[] = [];
  let current: Span | null = null;
  for (const atom of atoms) {
    const candidate: Span = current ? { start: current.start, end: atom.end } : atom;
    if (current && spanLength(source, candidate) > MAX_SEGMENT_LENGTH) {
      pieces.push(current);
      current = atom;
    } else {
      current = candidate;
    }
  }
  if (current) pieces.push(current);
  return pieces;
}

function splitIntoSentences(source: string, language: string): Span[] {
  const Segmenter = (Intl as typeof Intl & { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (typeof Segmenter === "function") {
    try {
      const segmenter = new Segmenter(normalizeLocale(language), { granularity: "sentence" });
      const parts: Span[] = [];
      for (const { segment, index } of segmenter.segment(source)) {
        const piece = trimSpan(source, index, index + segment.length);
        if (piece) parts.push(piece);
      }
      if (parts.length > 0) return parts;
    } catch {
      // Fall through to the regex path on any locale/runtime issue.
    }
  }
  // Fallback splitter: break after sentence-ending punctuation (incl. closing
  // quotes/brackets) when followed by whitespace.
  const whole = trimSpan(source, 0, source.length);
  return whole ? splitSpanAt(source, whole, /(?<=[.!?…][»"')\]]?)\s+/gu) : [];
}

// Merge a fragment that is too short to stand alone into the next segment (or the
// previous one if it is the last fragment), so steps stay readable.
function mergeShortSpans(source: string, spans: Span[]): Span[] {
  const merged: Span[] = [];
  let pending: Span | null = null;

  for (const span of spans) {
    const candidate: Span = pending ? { start: pending.start, end: span.end } : span;
    if (spanLength(source, candidate) < MIN_SEGMENT_LENGTH) {
      pending = candidate;
      continue;
    }
    merged.push(candidate);
    pending = null;
  }

  if (pending) {
    if (merged.length > 0) {
      merged[merged.length - 1] = { start: merged[merged.length - 1].start, end: pending.end };
    } else {
      merged.push(pending);
    }
  }

  return merged;
}

// Intl.Segmenter wants a BCP 47 tag; tolerate a bare language code or junk.
function normalizeLocale(language: string): string | undefined {
  const trimmed = language.trim();
  return trimmed || undefined;
}
